import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getMeasurementUnitDetails, createMeasurementUnit, updateMeasurementUnit } from '../../Controller/MeasurementUnitController';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { withNamespaces } from 'react-i18next';

/**
 * Shared form component for creating and editing measurement units
 * @param {Object} props - Component props
 * @returns {JSX.Element}
 */
const MeasurementUnitForm = ({ isEdit = false, slack = null, ...props }) => {
  const { t, i18n } = props;
  const history = useHistory();
  const { lng } = useParams();
  
  // Form state
  const [formData, setFormData] = useState({
    label: '',
    unit_code: '',
    status: '1'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  // Fetch measurement unit details if in edit mode
  useEffect(() => {
    if (isEdit && slack) {
      fetchMeasurementUnitDetails();
    }
  }, [isEdit, slack]);

  /**
   * Fetch measurement unit details for edit mode
   */
  const fetchMeasurementUnitDetails = async () => {
    setInitialLoading(true);
    try {
      const response = await getMeasurementUnitDetails(slack);
      
      if (response.success && response.data) {
        setFormData({
          label: response.data.label || '',
          unit_code: response.data.unit_code || '',
          status: response.data.status === 1 ? '1' : '0'
        });
      } else {
        Utility.toastNotifications(response.message || t('measurementUnits.form.errors.failedToFetchDetails'), 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching measurement unit details:', error);
      Utility.toastNotifications(t('measurementUnits.form.errors.errorFetchingDetails'), 'Error', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  /**
   * Handle input change
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  /**
   * Validate form inputs
   * @returns {boolean} - True if form is valid
   */
  const validateForm = () => {
    const newErrors = {};
    
    // Validate label field
    if (!formData.label.trim()) {
      newErrors.label = t('measurementUnits.form.validation.labelRequired');
    }
    
    // Validate unit_code field
    if (!formData.unit_code.trim()) {
      newErrors.unit_code = t('measurementUnits.form.validation.unitCodeRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log("Form submission started");
    const isValid = validateForm();
    console.log("Validation result:", isValid);
    
    if (!isValid) {
      console.log("Validation failed, stopping submission");
      return;
    }
    
    console.log("Validation passed, calling API");
    setLoading(true);
    try {
      let response;
      
      if (isEdit) {
        response = await updateMeasurementUnit(slack, formData);
      } else {
        response = await createMeasurementUnit(formData);
      }
      
      if (response.success) {
        Utility.toastNotifications(
          response.message || t(`measurementUnits.form.success.${isEdit ? 'updated' : 'created'}`),
          'Success',
          'success'
        );
        // Navigate back to list page
        history.push(`/${lng}/settings/measurement-units`);
      }
      // } else {
      //   console.log("API response:", response);
      //   // Let the catch block handle error messages
      //   throw new Error(response.message || t(`measurementUnits.form.errors.failedTo${isEdit ? 'Update' : 'Create'}`));
      // }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} measurement unit:`, error);
      // Utility.toastNotifications(
      //   error.message || t(`measurementUnits.form.errors.errorWhile${isEdit ? 'Updating' : 'Creating'}`),
      //   'Error',
      //   'error'
      // );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    history.push(`/${lng}/settings/measurement-units`);
  };

  return (
    <Card>
      <Card.Body>
        {initialLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">{t('measurementUnits.form.loading')}</span>
            </Spinner>
            <p className="mt-2">{t('measurementUnits.form.loadingDetails')}</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('measurementUnits.form.labels.label')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="label"
                      value={formData.label}
                      onChange={handleChange}
                      isInvalid={!!errors.label}
                      placeholder={t('measurementUnits.form.placeholders.label')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.label}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      {t('measurementUnits.form.helpText.labelExample')}
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('measurementUnits.form.labels.unitCode')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="unit_code"
                      value={formData.unit_code}
                      onChange={handleChange}
                      isInvalid={!!errors.unit_code}
                      placeholder={t('measurementUnits.form.placeholders.unitCode')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.unit_code}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      {t('measurementUnits.form.helpText.unitCodeExample')}
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('measurementUnits.form.labels.status')}</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="1">{t('measurementUnits.form.status.active')}</option>
                      <option value="0">{t('measurementUnits.form.status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={handleCancel} className="me-2" disabled={loading}>
                <FontAwesomeIcon icon={faTimes} className="me-1" /> {t('measurementUnits.form.buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                    {isEdit ? t('measurementUnits.form.buttons.updating') : t('measurementUnits.form.buttons.saving')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-1" /> {isEdit ? t('measurementUnits.form.buttons.update') : t('measurementUnits.form.buttons.save')}
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};


export default withNamespaces()(MeasurementUnitForm);
